/**
 * MASTER — PORTRAIT_DIALOGUE / «Портрет / Диалог».
 *
 * Reusable across every Digital Editions surface — paid Volumes (Almond
 * pilot, future volumes), Open Library, paid courses, Gingerbread Workshop
 * and future editorial collections. Nothing in this file is Almond, product,
 * material or route specific.
 *
 * Storage:
 *   All structured data lives inside `block.content.portrait_dialogue`
 *   (schema exposes `portrait_dialogue: Optional[Dict[str, Any]]`).
 *   Legacy top-level keys of `block.content` are preserved verbatim.
 *
 * Shared primitives reused from HeroMaster / CropModal — no second media
 * workflow is created here.
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  ArrowUp, ArrowDown, Trash2, Plus, User, ChevronLeft, ChevronRight,
} from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import {
  ImageSlot,
  MediaLibraryPicker,
} from "@/components/editorial/HeroMaster";

const genId = (p = "id") =>
  `${p}${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;

function readPortrait(rawContent) {
  const c = rawContent || {};
  const p = (c.portrait_dialogue && typeof c.portrait_dialogue === "object")
    ? c.portrait_dialogue : {};

  const readImg = (img) => ({
    media_id: img?.media_id ?? null,
    url:      img?.url      ?? null,
    alt:      img?.alt      ?? "",
    caption:  img?.caption  ?? "",
    focal_x:  img?.focal_x  ?? 0.5,
    focal_y:  img?.focal_y  ?? 0.5,
    zoom:     img?.zoom     ?? 1,
  });

  const qa = Array.isArray(p.qa) && p.qa.length
    ? p.qa.map((q) => ({
        id: q.id || genId("qa"),
        question: q.question || "",
        answer:   q.answer   || "",
      }))
    : [{ id: genId("qa"), question: "", answer: "" }];

  const side = p.image_side === "left" ? "left" : "right"; // default: right

  return {
    eyebrow:      p.eyebrow      ?? "",
    name:         p.name         ?? c.headline ?? "",
    role:         p.role         ?? "",
    intro:        p.intro        ?? c.lead     ?? "",
    portrait:        readImg(p.portrait),
    mobile_portrait: readImg(p.mobile_portrait),
    qa,
    quote:        p.quote        ?? c.quote     ?? "",
    quote_source: p.quote_source ?? c.signature ?? "",
    image_side:   side,
  };
}

// -------------------- Editor --------------------

export function PortraitDialogueEditor({ block, onPatch }) {
  const p = useMemo(() => readPortrait(block.content), [block.content]);
  const patch = (delta) => onPatch({
    content: { ...(block.content || {}), portrait_dialogue: { ...p, ...delta } },
  });

  const [mainLib, setMainLib]     = useState(false);
  const [mobileLib, setMobileLib] = useState(false);
  const [mainCrop, setMainCrop]   = useState(false);
  const [mobileCrop, setMobileCrop] = useState(false);

  const setPortrait       = (delta) => patch({ portrait:        { ...p.portrait,        ...delta } });
  const setMobilePortrait = (delta) => patch({ mobile_portrait: { ...p.mobile_portrait, ...delta } });

  // -- Q&A --
  const setQA = (qa) => patch({ qa });
  const addQA    = () => setQA([...p.qa, { id: genId("qa"), question: "", answer: "" }]);
  const removeQA = (i) => {
    const next = [...p.qa]; next.splice(i, 1);
    setQA(next.length ? next : [{ id: genId("qa"), question: "", answer: "" }]);
  };
  const moveQA = (i, dir) => {
    const next = [...p.qa]; const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setQA(next);
  };
  const patchQA = (i, delta) => {
    const next = [...p.qa]; next[i] = { ...next[i], ...delta };
    setQA(next);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4"
         data-testid="portrait-editor">
      {/* LEFT — text + Q&A */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Портрет / Диалог · текст</div>

        <div>
          <label className="block text-[11px] opacity-60 mb-1">Метка раздела · необязательно</label>
          <Input value={p.eyebrow}
                 onChange={(e) => patch({ eyebrow: e.target.value })}
                 placeholder="ИНТЕРВЬЮ · РАЗГОВОР"
                 data-testid="portrait-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Имя героя</label>
          <Input value={p.name}
                 onChange={(e) => patch({ name: e.target.value })}
                 className="font-serif text-lg"
                 data-testid="portrait-field-name" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Роль / должность · необязательно</label>
          <Input value={p.role}
                 onChange={(e) => patch({ role: e.target.value })}
                 placeholder="Шеф-кондитер, автор книги"
                 data-testid="portrait-field-role" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Введение · необязательно</label>
          <textarea rows={3} value={p.intro}
                    onChange={(e) => patch({ intro: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic font-serif leading-relaxed"
                    placeholder="Короткое представление героя перед диалогом…"
                    data-testid="portrait-field-intro" />
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest opacity-70">Вопросы и ответы</div>
            <button type="button" onClick={addQA}
              className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
              data-testid="portrait-add-qa">
              <Plus className="w-3.5 h-3.5" /> Добавить вопрос
            </button>
          </div>
          <div className="space-y-2">
            {p.qa.map((q, i) => (
              <div key={q.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-2"
                   data-testid={`portrait-qa-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest opacity-60 shrink-0 font-mono">
                    Q{String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1" />
                  <button type="button" onClick={() => moveQA(i, -1)} disabled={i === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`portrait-qa-up-${i}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => moveQA(i, +1)} disabled={i === p.qa.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`portrait-qa-down-${i}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => removeQA(i)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить" data-testid={`portrait-qa-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <Input value={q.question}
                       onChange={(e) => patchQA(i, { question: e.target.value })}
                       className="font-serif"
                       placeholder="Вопрос"
                       data-testid={`portrait-qa-question-${i}`} />
                <textarea rows={3} value={q.answer}
                          onChange={(e) => patchQA(i, { answer: e.target.value })}
                          className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                          placeholder="Ответ героя"
                          data-testid={`portrait-qa-answer-${i}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Авторская цитата · необязательно</div>
          <textarea rows={2} value={p.quote}
                    onChange={(e) => patch({ quote: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic font-serif"
                    placeholder="Одна фраза героя, задающая тон разговора"
                    data-testid="portrait-field-quote" />
          <div className="mt-2">
            <label className="block text-[11px] opacity-60 mb-1">Источник · подпись · необязательно</label>
            <Input value={p.quote_source}
                   onChange={(e) => patch({ quote_source: e.target.value })}
                   placeholder="Автор или контекст цитаты"
                   data-testid="portrait-field-quote-source" />
          </div>
        </div>
      </div>

      {/* RIGHT — portrait + mobile portrait + image side */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Портрет</div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[11px] opacity-60">Основной портрет · desktop</div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-widest opacity-60">Сторона</span>
              <button type="button"
                onClick={() => patch({ image_side: "left" })}
                className={`p-1 rounded border ${p.image_side === "left" ? "bg-espresso text-cream border-espresso" : "opacity-60 hover:opacity-100"}`}
                title="Слева" data-testid="portrait-side-left">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button type="button"
                onClick={() => patch({ image_side: "right" })}
                className={`p-1 rounded border ${p.image_side === "right" ? "bg-espresso text-cream border-espresso" : "opacity-60 hover:opacity-100"}`}
                title="Справа" data-testid="portrait-side-right">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <ImageSlot
            label=""
            url={p.portrait.url}
            focalX={p.portrait.focal_x} focalY={p.portrait.focal_y} zoom={p.portrait.zoom}
            onFile={(x) => setPortrait({ media_id: x.id, url: x.file_url })}
            onClear={() => setPortrait({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMainLib(true)}
            onOpenCrop={() => setMainCrop(true)}
            testid="portrait-main-image" />
          <div className="mt-2 grid grid-cols-1 gap-2">
            <Input placeholder="Alt-текст · необязательно"
                   value={p.portrait.alt}
                   onChange={(e) => setPortrait({ alt: e.target.value })}
                   data-testid="portrait-main-alt" />
            <Input placeholder="Подпись под портретом · необязательно"
                   value={p.portrait.caption}
                   onChange={(e) => setPortrait({ caption: e.target.value })}
                   data-testid="portrait-main-caption" />
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="text-[11px] opacity-60 mb-2">
            Мобильный портрет · необязательно
            <span className="ml-1 opacity-70">(если пусто — используется основной)</span>
          </div>
          <ImageSlot
            label=""
            url={p.mobile_portrait.url}
            focalX={p.mobile_portrait.focal_x} focalY={p.mobile_portrait.focal_y} zoom={p.mobile_portrait.zoom}
            onFile={(x) => setMobilePortrait({ media_id: x.id, url: x.file_url })}
            onClear={() => setMobilePortrait({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMobileLib(true)}
            onOpenCrop={() => setMobileCrop(true)}
            testid="portrait-mobile-image" />
          <div className="mt-2 grid grid-cols-1 gap-2">
            <Input placeholder="Alt-текст · необязательно"
                   value={p.mobile_portrait.alt}
                   onChange={(e) => setMobilePortrait({ alt: e.target.value })}
                   data-testid="portrait-mobile-alt" />
            <Input placeholder="Подпись · необязательно"
                   value={p.mobile_portrait.caption}
                   onChange={(e) => setMobilePortrait({ caption: e.target.value })}
                   data-testid="portrait-mobile-caption" />
          </div>
        </div>
      </div>

      <MediaLibraryPicker open={mainLib} onClose={() => setMainLib(false)}
        onPick={(x) => { setPortrait({ media_id: x.id, url: x.file_url || x.url }); setMainLib(false); }} />
      <MediaLibraryPicker open={mobileLib} onClose={() => setMobileLib(false)}
        onPick={(x) => { setMobilePortrait({ media_id: x.id, url: x.file_url || x.url }); setMobileLib(false); }} />
      <CropModal open={mainCrop}
        url={p.portrait.url}
        focalX={p.portrait.focal_x} focalY={p.portrait.focal_y} zoom={p.portrait.zoom}
        onClose={() => setMainCrop(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setPortrait({ focal_x, focal_y, zoom }); setMainCrop(false); }} />
      <CropModal open={mobileCrop}
        url={p.mobile_portrait.url}
        focalX={p.mobile_portrait.focal_x} focalY={p.mobile_portrait.focal_y} zoom={p.mobile_portrait.zoom}
        onClose={() => setMobileCrop(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setMobilePortrait({ focal_x, focal_y, zoom }); setMobileCrop(false); }} />
    </div>
  );
}

// -------------------- Render --------------------

function PortraitFrame({ img }) {
  if (!img?.url) return null;
  const fx = (img.focal_x ?? 0.5) * 100;
  const fy = (img.focal_y ?? 0.5) * 100;
  return (
    <figure className="w-full" data-testid="portrait-image">
      <div className="overflow-hidden bg-neutral-100 aspect-[4/5]">
        <img src={img.url} alt={img.alt || ""}
             className="w-full h-full object-cover"
             style={{
               objectPosition: `${fx}% ${fy}%`,
               transform: `scale(${img.zoom || 1})`,
               transformOrigin: `${fx}% ${fy}%`,
             }} />
      </div>
      {img.caption && (
        <figcaption className="mt-3 text-[12px] italic font-serif leading-[1.5] max-w-[36ch]"
                    style={{ color: "#5B4326" }}
                    data-testid="portrait-image-caption">
          {img.caption}
        </figcaption>
      )}
    </figure>
  );
}

export function PortraitDialogueRender({ block, mode = "desktop" }) {
  const p = useMemo(() => readPortrait(block.content), [block.content]);
  const isMobile = mode === "mobile";

  const activeImage = isMobile
    ? (p.mobile_portrait.url ? p.mobile_portrait : p.portrait)
    : p.portrait;
  const hasImage = !!(activeImage && activeImage.url);

  const qa = (p.qa || []).filter((q) => (q.question && q.question.trim()) || (q.answer && q.answer.trim()));

  const Eyebrow = p.eyebrow ? (
    <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.28em] uppercase"
         style={{ color: "#8A6A46" }}
         data-testid="portrait-eyebrow">
      <User className="w-3.5 h-3.5" />
      {p.eyebrow}
    </div>
  ) : null;

  const Name = p.name ? (
    <h2 className="font-serif leading-[1.05]"
        style={{
          fontSize: isMobile ? "clamp(28px, 6.4vw, 34px)" : "clamp(32px, 3.2vw, 44px)",
          color: "#2A1D14",
          letterSpacing: "-0.005em",
        }}
        data-testid="portrait-name">
      {p.name}
    </h2>
  ) : null;

  const Role = p.role ? (
    <div className="mt-2 text-[11px] tracking-[0.24em] uppercase" style={{ color: "#8A6A46" }}
         data-testid="portrait-role">
      {p.role}
    </div>
  ) : null;

  const Intro = p.intro ? (
    <p className="mt-4 font-serif italic text-[16px] leading-[1.6] max-w-[56ch]"
       style={{ color: "#5B4326" }}
       data-testid="portrait-intro">
      {p.intro}
    </p>
  ) : null;

  const QASection = qa.length ? (
    <div className={isMobile ? "mt-8 space-y-6" : "mt-10 space-y-8"}
         data-testid="portrait-qa-section">
      {qa.map((q) => (
        <div key={q.id} data-testid={`portrait-qa-item-${q.id}`}>
          {q.question && (
            <div className="flex items-baseline gap-3">
              <span className="text-[10px] tracking-[0.28em] uppercase mt-1 shrink-0"
                    style={{ color: "#A67B4A" }}>—&nbsp;В</span>
              <h4 className="font-serif leading-[1.3]"
                  style={{
                    fontSize: isMobile ? "clamp(17px, 4.8vw, 19px)" : "clamp(19px, 1.6vw, 22px)",
                    color: "#2A1D14",
                  }}>
                {q.question}
              </h4>
            </div>
          )}
          {q.answer && (
            <div className={`${q.question ? "mt-3" : ""} flex items-baseline gap-3`}>
              <span className="text-[10px] tracking-[0.28em] uppercase mt-1 shrink-0"
                    style={{ color: "#5B4326" }}>—&nbsp;О</span>
              <p className="font-serif text-[16px] leading-[1.75] whitespace-pre-wrap max-w-[62ch]"
                 style={{ color: "#2A1D14" }}>
                {q.answer}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  ) : null;

  const Quote = (p.quote && p.quote.trim()) ? (
    <div className={isMobile ? "mt-8 pl-4 border-l-2" : "mt-10 pl-4 border-l-2"}
         style={{ borderColor: "#A67B4A" }}
         data-testid="portrait-quote">
      <p className="font-serif italic leading-[1.5] max-w-[56ch]"
         style={{
           fontSize: isMobile ? "18px" : "clamp(18px, 1.6vw, 22px)",
           color: "#3E2E20",
         }}>
        {`«${p.quote.trim().replace(/^[«"]|[»"]$/g, "")}»`}
      </p>
      {p.quote_source && (
        <div className="mt-2 text-[11px] tracking-[0.2em] uppercase" style={{ color: "#8A6A46" }}>
          — {p.quote_source}
        </div>
      )}
    </div>
  ) : null;

  const TextColumn = (
    <div>
      {Eyebrow}
      {Name}
      {Role}
      {Intro}
      {QASection}
      {Quote}
    </div>
  );

  const ImageColumn = hasImage ? (
    <div><PortraitFrame img={activeImage} /></div>
  ) : null;

  return (
    <section className="w-full overflow-hidden"
             style={{ background: "#EFE6D5" }}
             data-testid={`portrait-render-${block.id}-${mode}`}>
      <div className={`mx-auto px-6 sm:px-10 py-12 sm:py-16 ${isMobile ? "" : "max-w-[1200px]"}`}>
        {isMobile ? (
          <>
            {Eyebrow}
            {Name}
            {Role}
            {hasImage && (
              <div className="mt-6">
                <PortraitFrame img={activeImage} />
              </div>
            )}
            {Intro}
            {QASection}
            {Quote}
          </>
        ) : hasImage ? (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,40fr)_minmax(0,60fr)] gap-12 lg:gap-16 items-start">
            {p.image_side === "left" ? <>{ImageColumn}{TextColumn}</> : <>{TextColumn}{ImageColumn}</>}
          </div>
        ) : (
          <div className="mx-auto max-w-[760px]">
            {TextColumn}
          </div>
        )}
      </div>
    </section>
  );
}
